import React, { Children } from "react"
import { uniqBy, orderBy } from "lodash"

// Utils
import { generateCustomFieldsColumns } from "Components/Governance/CustomFields/utils.js"
import { getUserNameById } from "Components/Governance/utils.js"
import { getFirstLetterUppercase } from "Utils/global.js"
import {
  getDefaultCriticalityOptions,
  getCustomCriticalityOptions,
  findSelectedCriticality,
} from "../AnomalieInWork/utils/criticality"

// Styles
import {
  TableColumsOption,
  ContainerBubble,
  BubbleExample,
  OptionButtons,
} from "Components/Governance/Anomalies/index.style"

export const getNonConformitiesColumn = (
  customNonConformitiesCriticality,
  accessIncidentManagement,
  controlListNotArchived,
  onValidNonConformity,
  primaryActionColor,
  setModalToDelete,
  onRowSelected,
  tableSkeleton,
  businessUsers,
  customFields,
  choicesList,
  activities,
  actualTab,
  userRole,
  isAdmin,
  risks,
  trad
) => {
  const tableSkeletonExisted = Boolean(tableSkeleton?._id)
  const skeletonColumns = tableSkeleton?.columns
  const skeletonOrder = tableSkeleton?.columns_order

  //
  const incidentColumn = actualTab === "incident" && [
    {
      headerName: trad?.ACTIVTY_TAB?.toUpperCase(),
      field: "activitiesIds",
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => {
        return (
          <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize">
            {Children.toArray(
              getControlsByIds(activities, cell?.data?.activitiesIds)?.map((activitie, index) => (
                <ContainerBubble key={index}>
                  <BubbleExample
                    title={activitie?.reference}
                    backgroundColor={primaryActionColor}
                    key={`${activitie?._id?.$oid}`}
                  >
                    {activitie?.reference}
                  </BubbleExample>
                </ContainerBubble>
              ))
            )}
          </div>
        )
      },
    },
    {
      headerName: trad?.RISK_TAB?.toUpperCase(),
      field: "risksIds",
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => {
        return (
          <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize">
            {Children.toArray(
              getControlsByIds(risks, cell?.data?.risksIds)?.map((risk, index) => (
                <ContainerBubble key={index}>
                  <BubbleExample
                    title={risk?.reference}
                    backgroundColor={primaryActionColor}
                    key={`${risk?._id?.$oid}`}
                  >
                    {risk?.reference}
                  </BubbleExample>
                </ContainerBubble>
              ))
            )}
          </div>
        )
      },
    },
  ]

  const auditPlanColumn = actualTab === "auditRecommendation" && [
    {
      headerName: trad?.AUDIT_PLAN?.toUpperCase(),
      field: "auditPlanName",
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => {
        return (
          <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize centerCell">
            {cell?.value}
          </div>
        )
      },
    },
  ]

  const customFieldsColumns = generateCustomFieldsColumns(customFields, choicesList, onRowSelected)

  const recommendationColumn = [
    {
      headerName: trad?.PROCESS?.toUpperCase(),
      field: "process",
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => {
        const controls = cell?.data?.controls

        return (
          <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize">
            {Array.isArray(controls) &&
              Children.toArray(
                controls.map(control => {
                  return <p className="textOverflowCell"> {control?.process}</p>
                })
              )}
          </div>
        )
      },
    },
    {
      headerName: trad?.riskLabelNameRisk?.toUpperCase(),
      field: "riskName",
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => {
        const controls = cell?.data?.controls

        return (
          <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize">
            {Array.isArray(controls) &&
              Children.toArray(
                controls.map(control => {
                  return getControlsByIds(risks, control?.riskIds)?.map((risk, index) => (
                    <ContainerBubble key={index}>
                      <BubbleExample
                        title={risk?.reference}
                        backgroundColor={primaryActionColor}
                        key={`${risk?._id?.$oid}`}
                      >
                        {risk?.reference}
                      </BubbleExample>
                    </ContainerBubble>
                  ))
                })
              )}
          </div>
        )
      },
    },
    {
      headerName: trad?.riskLabelActiviteRisk?.toUpperCase(),
      field: "activityName",
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => {
        const controls = cell?.data?.controls

        return (
          <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize">
            {Array.isArray(controls) &&
              Children.toArray(
                controls.map(control => {
                  const risques = getControlsByIds(risks, control?.riskIds)

                  return risques?.map(risque => {
                    return getControlsByIds(activities, [risque?.activity])?.map(
                      (activitie, index) => (
                        <ContainerBubble key={index}>
                          <BubbleExample
                            title={activitie?.reference}
                            backgroundColor={primaryActionColor}
                            key={`${activitie?._id?.$oid}`}
                          >
                            {activitie?.reference}
                          </BubbleExample>
                        </ContainerBubble>
                      )
                    )
                  })
                })
              )}
          </div>
        )
      },
    },
    {
      headerName: trad?.CONTROLEUR?.toUpperCase(),
      field: "controller",
      minWidth: 150,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => (
        <div
          title={getUserNameById(cell?.data?.controller)}
          onClick={() => onRowSelected(cell, actualTab)}
          className="cellFullSize centerCell"
        >
          <p className="textOverflowCell">
            {getUserNameById(cell?.data?.controller, businessUsers)}
          </p>
        </div>
      ),
    },
    {
      headerName: trad?.AFFECTATION?.toUpperCase(),
      field: "affectation",
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => {
        const controls = cell?.data?.controls

        return (
          <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize">
            {Array.isArray(controls) &&
              Children.toArray(
                controls.map(control => {
                  const usersIds = control?.targetUsers?.usersIds
                  if (Array.isArray(usersIds)) {
                    return usersIds?.map(userId => {
                      const user = businessUsers?.find(user => user?.USER_ID === userId)
                      return (
                        <p className="textOverflowCell">{`${user?.FIRSTNAME} ${user?.LASTNAME}`}</p>
                      )
                    })
                  }
                })
              )}
          </div>
        )
      },
    },
    {
      headerName: trad?.RECOMMENDATION?.toUpperCase(),
      field: "name",
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => (
        <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize centerCell">
          <ContainerBubble>
            <BubbleExample title={cell?.data?.name} backgroundColor={primaryActionColor}>
              {cell?.data?.name}
            </BubbleExample>
          </ContainerBubble>
        </div>
      ),
    },
    {
      headerName: trad?.LAST_MISE_A_JOUR?.toUpperCase(),
      field: "date_update",
      minWidth: 150,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: cell => (
        <div
          title={transformDate(cell?.data?.updated_at)}
          onClick={() => onRowSelected(cell, actualTab)}
          className="cellFullSize centerCell"
        >
          <p className="textOverflowCell">{transformDate(cell?.data?.updated_at)}</p>
        </div>
      ),
    },
    {
      headerName: trad?.ANOMALIE_STATUS?.toUpperCase(),
      field: "status",
      cellStyle: { display: "flex", justifyContent: "center" },
      autoHeight: true,
      cellRenderer: cell => (
        <div
          onClick={() => onRowSelected(cell, actualTab)}
          className="cellFullSize centerCell flexWrap"
        >
          {getTradStatusAnoOrIncident(trad, cell?.value)}
        </div>
      ),
    },
  ]

  const columns =
    actualTab === "recommandation"
      ? recommendationColumn
      : [
          {
            headerName: trad?.ANOMALIE_REFERENCE?.toUpperCase(),
            field: "name",
            minWidth: 150,
            cellStyle: { display: "flex", justifyContent: "center" },
            cellRenderer: cell => (
              <div
                title={cell?.data?.name}
                onClick={() => onRowSelected(cell, actualTab)}
                className="cellFullSize centerCell"
              >
                <p className="textOverflowCell"> {cell?.data?.name}</p>
              </div>
            ),
          },
          ...(actualTab === "auditRecommendation" ? auditPlanColumn : []),
          {
            headerName: `${trad?.CREATOR?.toUpperCase()}`,
            field: "creatorId",
            cellStyle: { display: "flex", justifyContent: "center" },
            cellRenderer: cell => (
              <div
                onClick={() => onRowSelected(cell, actualTab)}
                className="cellFullSize centerCell"
              >
                {cell?.data?.creator?.LASTNAME} {cell?.data?.creator?.FIRSTNAME}
              </div>
            ),
          },
          {
            headerName: `${trad?.CREATED_DATE?.toUpperCase()}`,
            field: "creationDate",
            cellStyle: { display: "flex", justifyContent: "center" },
            cellRenderer: cell => {
              const dateToUse = cell?.data?.creationDate
                ? cell?.data?.creationDate
                : cell?.data?.createdDate
              const formatedDate = new Date(dateToUse).toLocaleDateString("fr")
              return (
                <div
                  onClick={() => onRowSelected(cell, actualTab)}
                  className="cellFullSize centerCell"
                >
                  {formatedDate}
                </div>
              )
            },
          },
          {
            headerName: `${trad?.CRITICALITY?.toUpperCase()}`,
            field: "criticality",
            cellStyle: { display: "flex", justifyContent: "center" },
            cellRenderer: cell => {
              const nonConformity = cell?.data
              const defaultOptions = getDefaultCriticalityOptions(trad)
              const customOptions = getCustomCriticalityOptions(customNonConformitiesCriticality)
              const selectedCriticality = findSelectedCriticality(
                defaultOptions,
                customOptions,
                nonConformity,
                trad
              )
              const selectedCriticalityFormated =
                getFirstLetterUppercase(selectedCriticality?.label) || ""
              return (
                <div
                  onClick={() => onRowSelected(cell, actualTab)}
                  className="cellFullSize centerCell"
                >
                  {selectedCriticalityFormated}
                </div>
              )
            },
          },
          {
            headerName: trad?.ANOMALIE_CONTROLE?.toUpperCase(),
            field: "controlIds",
            wrapText: true,
            autoHeight: true,
            cellStyle: { display: "flex", justifyContent: "center" },
            cellRenderer: cell => {
              const controls = getControlsByIds(controlListNotArchived, cell?.data?.controlIds)

              return (
                <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize">
                  {Children.toArray(
                    controls?.map((control, index) => (
                      <ContainerBubble key={index}>
                        <BubbleExample
                          title={control?.reference}
                          backgroundColor={control?.color}
                          key={`${control?._id?.$oid}`}
                        >
                          {control?.reference}
                        </BubbleExample>
                      </ContainerBubble>
                    ))
                  )}
                </div>
              )
            },
          },
          {
            headerName: trad?.CARTOGRAPHIE?.toUpperCase(),
            field: "mappingRisks",
            wrapText: true,
            autoHeight: true,
            cellStyle: { display: "flex", justifyContent: "center" },
            cellRenderer: cell => {
              const mappingRisks = extractMappingRisksToControls(cell?.data, false)

              return (
                <div onClick={() => onRowSelected(cell, actualTab)} className="cellFullSize">
                  {Children.toArray(mappingRisks?.map(carto => <p>{carto?.name}</p>))}
                </div>
              )
            },
          },
          ...(actualTab === "incident" ? incidentColumn : []),
          {
            headerName: trad?.TASKS?.toUpperCase(),
            field: "numberOfTasks",
            cellStyle: { display: "flex", justifyContent: "center" },
            cellRenderer: cell => (
              <div
                onClick={() => onRowSelected(cell, actualTab)}
                className="cellFullSize centerCell"
              >
                {cell?.data?.numberOfTasks}
              </div>
            ),
          },
          {
            headerName: trad?.ANOMALIE_STATUS?.toUpperCase(),
            field: "status",
            cellStyle: { display: "flex", justifyContent: "center" },
            autoHeight: true,
            cellRenderer: cell => (
              <div
                onClick={() => onRowSelected(cell, actualTab)}
                className="cellFullSize centerCell flexWrap"
              >
                {getTradStatusAnoOrIncident(trad, cell?.value)}
              </div>
            ),
          },
          ...(customFieldsColumns?.length ? customFieldsColumns : []),
        ]

  const optionsColumn = {
    headerName: `${""}`,
    field: "options",
    minWidth: 120,
    maxWidth: 120,
    cellRenderer: cell => {
      const canDeleteNonConformity = Boolean(userRole?.deleteIncident)
      return (
        <TableColumsOption>
          {accessIncidentManagement &&
          cell?.data?.status === "waitingForAdminValidation" &&
          isAdmin ? (
            <OptionButtons
              title={trad?.VALIDATE}
              onClick={() =>
                onValidNonConformity({
                  nonConformity: cell?.data,
                })
              }
            >
              <i className="fas fa-check" />
            </OptionButtons>
          ) : (
            <div></div>
          )}
          {isAdmin && canDeleteNonConformity ? (
            <OptionButtons title="Supprimer">
              <i
                className="fas fa-trash"
                onClick={() =>
                  setModalToDelete({
                    isOpen: true,
                    nonConformityId: cell?.data?._id?.$oid,
                  })
                }
              />
            </OptionButtons>
          ) : (
            <div></div>
          )}
        </TableColumsOption>
      )
    },
  }

  if (tableSkeletonExisted) {
    const orderedColumns = reorderColumns(columns, skeletonOrder)

    const newColumns = orderedColumns
      ?.map(col => {
        const currentColumn = skeletonColumns?.find(skt => skt?.column_name === col?.field)

        // Si c'est une column de table skeleton
        if (currentColumn?.hasOwnProperty("active")) {
          if (currentColumn?.active) return col
          else return null
        } else {
          return col
        }
      })
      ?.filter(col => col !== null)

    return [...newColumns, optionsColumn]
  } else {
    return [...columns, optionsColumn]
  }
}

function extractMappingRisksToControls(control) {
  const mappingRisksTmp =
    control?.controls?.flatMap(fullControl => fullControl?.mappingRisksAssociated || []) || []

  return orderBy(uniqBy(mappingRisksTmp, "name"), "name")
}

export const transformDate = (inputDate, withoutYear = false, formatYYYYMMDD = false) => {
  if (!inputDate) return ""
  const dateParts = inputDate.split(/[-T\s:.]/)
  const year = dateParts[0]
  const month = dateParts[1]
  const day = dateParts[2]

  if (withoutYear) {
    return `${day}/${month}`
  }
  if (formatYYYYMMDD) {
    return `${year}-${month}-${day}`
  }
  return `${day}/${month}/${year}`
}

function reorderColumns(columns, order) {
  const orderedColumns = []

  // Map pour créer un objet avec colId comme clé
  const columnMap = columns.reduce((acc, column) => {
    acc[column.field] = column
    return acc
  }, {})

  // Parcours de l'ordre spécifié et ajout des colonnes dans l'ordre
  order.forEach(item => {
    const column = columnMap[item.colId]
    if (column) {
      orderedColumns.push(column)
      delete columnMap[item.colId] // Suppression pour éviter les doublons
    }
  })

  // Ajout des colonnes restantes à la fin
  const remainingColumns = Object.values(columnMap)
  orderedColumns.push(...remainingColumns)

  return orderedColumns
}

export const getDataAccordingTab = (
  fullAnomalies,
  fullIncidents,
  fullAuditRecommendations,
  recommendations,
  templatesList,
  tab
) => {
  let nonConformitiesToUse = []
  if (tab === "anomaly") nonConformitiesToUse = fullAnomalies
  else if (tab === "incident") nonConformitiesToUse = fullIncidents
  else if (tab === "auditRecommendation") {
    if (Array.isArray(fullAuditRecommendations)) {
      nonConformitiesToUse = fullAuditRecommendations.filter(
        fullReco => fullReco?.nonConformity?.status !== "waitingForAuditPlanValidation"
      )
    }
  } else if (tab === "recommandation") {
    if (Array.isArray(templatesList)) {
      nonConformitiesToUse = recommendations
    }
  }

  const nonConformitiesReturn = nonConformitiesToUse?.map(fullNonConformity => {
    const status =
      fullNonConformity?.nonConformity?.status === "rejected"
        ? fullNonConformity?.nonConformity?.status
        : fullNonConformity?.actionPlan?.status || fullNonConformity?.nonConformity?.status

    return {
      ...fullNonConformity?.nonConformity,
      status: status,
      actionPlanId: fullNonConformity?.actionPlan?._id?.$oid,
      numberOfTasks: fullNonConformity?.tasks?.length,
    }
  })

  return nonConformitiesReturn
}

export const filterComplicationByControlIds = (data, filters) => {
  let result = []

  for (let item of data) {
    for (let controlId of item?.controlIds) {
      for (let filter of filters) {
        if (controlId === filter?.value) result.push(item)
      }
    }
  }

  return uniqBy(result, "_id")
}

export const getNonConformityTranslation = trad => {
  return {
    veryLow: trad.VERYLOW,
    low: trad.LOW,
    moderate: trad.MODERATE,
    high: trad.HIGH,
    critic: trad.CRITICAL,
  }
}

export const getTradStatusAnoOrIncident = (trad, value) => {
  switch (value) {
    case "created":
    case "added":
      return trad?.CREATED
    case "inProgress":
      return trad?.IN_PROGRESS
    case "waitingForAuditPlanValidation":
      return trad?.WAITING_AUDIT_PLAN_VALIDATION
    case "waitingForAdminValidation":
      return trad?.WAITING_FOR_ADMIN_VALIDATION
    case "validateByAdmin":
      // mais on prefere "taches à réaliser", donc la clé ne correspond plus trop à la trad
      return trad?.TASKS_TO_BE_CARRIED
    case "awaitingTasksValidation":
      return trad?.AWAITING_ADMIN_TASKS_VALIDATION
    case "awaitingAuditorTasksValidation":
      return trad?.AWAITING_AUDITOR_TASKS_VALIDATION
    case "closed":
      return trad?.CLOSED
    case "rejected":
      return trad?.REJECTED_FEMININ
    case "done":
      return trad?.DONE
    case "autoValidate":
    case "open":
      return trad?.TASKS_TO_BE_CARRIED
    default:
      return value
  }
}

export const getTradCriticityAnoOrIncident = (trad, value) => {
  switch (value) {
    case "veryLow":
      return trad?.VERYLOW
    case "low":
      return trad?.LOW
    case "moderate":
      return trad?.MODERATE
    case "high":
      return trad?.HIGH
    case "critic":
      return trad?.CRITICAL
    default:
      return ""
  }
}

export const getTasksWithUserName = (tasks, users) => {
  if (!Array.isArray(tasks)) return []
  return tasks.map(task => {
    return {
      ...task,
      userName: getUserNameById(task?.inChargeUserId, users),
    }
  })
}

const getEmbeddedControls = (templateList, controlIds) => {
  let controls = []
  if (!Array.isArray(templateList)) return

  controlIds.forEach(controlId => {
    const controlFound = templateList.find(list => {
      return list.control._id.$oid === controlId
    })

    if (controlFound) {
      controls.push({
        value: controlFound.control._id.$oid,
        label: controlFound.control.process,
        reference: controlFound.control.reference,
        id: controlFound.control._id.$oid,
      })
    }
  })

  return controls
}

export const getInitialAssociatedControl = (anomaly, incident, templateList) => {
  if (anomaly?.controlIds?.length > 0) {
    var embeddedControls = getEmbeddedControls(templateList, anomaly?.controlIds)
    return embeddedControls
  }

  if (incident?.controlIds?.length > 0) {
    var embeddedControls = getEmbeddedControls(templateList, incident?.controlIds)
    return embeddedControls
  }

  return []
}

export const getCommentFromAnomalyOrIncident = (
  incidents,
  fullAnomaly,
  isIncidentId,
  isAnomaly
) => {
  if (isIncidentId) {
    return incidents?.comment
  } else if (isAnomaly) {
    return fullAnomaly?.anomaly?.comment
  }
}

export const getControlsByIds = (controls, ids) => {
  if (controls?.length && ids?.length) {
    let result = []

    for (let control of controls) {
      for (let id of ids) {
        if (control?._id?.$oid === id) result.push(control)
      }
    }

    return result
  }
}

export const getEmbeddedDataToDisplay = anoOrIncList => {
  if (!Array.isArray(anoOrIncList) && anoOrIncList === undefined) return []
  return anoOrIncList.map(row => ({
    ...row,
  }))
}

export const getNonConformityPrefix = type => {
  if (type === "anomaly") return "ANO"
  else if (type === "incident") return "INC"
  else if (type === "auditRecommendation") return "RECO"
  else if (type === "recommandation") return "RECO"
  else return "ANO"
}

export const getDeleteTextNonConformity = (actualTab, trad) => {
  if (!trad) return "Voulez vous vraiment supprimer cette non-conformité ?"
  if (actualTab === "anomaly") return trad?.CONFIRMATION_DELETE_ANOMALIE
  else if (actualTab === "incident") return trad?.CONFIRMATION_DELETE_INCIDENT
  else if (actualTab === "auditRecommendation") return trad?.CONFIRMATION_DELETE_AUDIT_RECO
  else if (actualTab === "recommandation") return trad?.CONFIRMATION_DELETE_RECO
  else return trad?.CONFIRMATION_DELETE_ANOMALIE
}

export const getIfBusinessUseNonConformities = (myBusiness, type) => {
  if (!myBusiness || !type) return false
  const businessUseAnomalies = myBusiness?.paramsNonConformities?.useAnomalies
  const businessUseIncidents = myBusiness?.paramsNonConformities?.useIncidents
  const businessUseRecommendations = myBusiness?.paramsNonConformities?.useRecommendations

  if (type === "anomaly" && businessUseAnomalies !== undefined) return businessUseAnomalies
  if (type === "incident" && businessUseIncidents !== undefined) return businessUseIncidents
  if (type === "recommandation" && businessUseRecommendations !== undefined)
    return businessUseRecommendations
  return true
}

export const getLabelBtnCreationNonConformity = (actualTab, trad) => {
  if (!actualTab || !trad) return ""
  if (actualTab === "anomaly") return trad?.ADD_ANOMALY
  if (actualTab === "incident") return trad?.ADD_INCIDENT
  if (actualTab === "auditRecommendation") return trad?.ADD_A_RECOMMENDATION
  if (actualTab === "recommandation") return trad?.ADD_A_RECOMMENDATION
  return trad?.ADD_ANOMALY
}

export const getInitialTabNonConformities = (router, myBusiness) => {
  if (!router || !myBusiness) return ""
  const nonConformityTypeFromRouter = router?.location?.state?.type
  if (nonConformityTypeFromRouter) return nonConformityTypeFromRouter
  const businessUseAnomalies = myBusiness?.paramsNonConformities?.useAnomalies
  const businessUseIncidents = myBusiness?.paramsNonConformities?.useIncidents
  const businessUseAuditPlans = myBusiness?.useAuditPlans
  if (businessUseAnomalies) return "anomaly"
  if (businessUseIncidents) return "incident"
  if (businessUseAuditPlans) return "auditRecommendation"
  return ""
}

// NOT USED 07/07/23
// export const getIsoDateFromDDMMYYYY = DDMMYYYY => moment(DDMMYYYY, "DD/MM/YYYY").toDate()
// export const getNewNonConformityReference = (nonConformities, type) => {
//   let nonConformitiesWithIsoDate = nonConformities.map(nonConformity => {
//     const dateToUse = nonConformity.creationDate
//       ? nonConformity.creationDate
//       : nonConformity.createdDate
//     return {
//       ...nonConformity,
//       creationDateIso: getIsoDateFromDDMMYYYY(dateToUse),
//     }
//   })

//   const orderedNonConformities = orderBy(nonConformitiesWithIsoDate, "creationDateIso", "desc")
//   const prefix = getNonConformityPrefix(type)
//   let lastRefName = `${prefix}00`
//   if (orderedNonConformities.length > 0 && orderedNonConformities[0].name) {
//     lastRefName = orderedNonConformities[0]?.name
//   }

//   const nameWithoutPrefix = lastRefName.split(prefix)?.[1]
//   const lastRefExtractedNumber = parseInt(nameWithoutPrefix)

//   let newRefNumber = lastRefExtractedNumber + 1
//   if (isNaN(lastRefExtractedNumber)) newRefNumber = 0

//   let newRefNumberAsStr = newRefNumber
//   if (newRefNumber < 10) newRefNumberAsStr = `0${newRefNumber}`
//   const newRef = `${prefix}${newRefNumberAsStr}`

//   return newRef
// }
